<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxSorting,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxSelectBox } from 'devextreme-vue/select-box';
import DxTabPanel, { DxItem as DxTabItem } from 'devextreme-vue/tab-panel';
import DxForm, { DxSimpleItem, DxLabel } from 'devextreme-vue/form';
import { BottoPagePanel, ConfirmDialog, bottoGridPaging, showError, showSuccess } from '@botto/core';
import { setTariffListFilters, tariffsStore } from '../api';
import type {
  ByCurrency,
  CallPriceEntry,
  SmsPriceEntry,
  Tariff,
  TariffCreateRequest,
  TariffPricesPayload,
  TariffUpdateRequest,
} from '../api';
import SmsPricesEditor from './editors/SmsPricesEditor.vue';
import CallsPricesEditor from './editors/CallsPricesEditor.vue';

interface TariffLimits {
  sms_speed: number;
  call_speed: number;
  max_mailings: number;
  max_rent_phones: number;
}

interface SmppLimits {
  api_limit: number;
  smpp_limit: number;
}

interface TariffFormModel {
  id?: number;
  name: string;
  type?: string | null;
  group?: string | null;
  status: 'active' | 'archived';
  is_default: boolean;
  limits: TariffLimits;
  api_smpp_limits: SmppLimits;
  prices: TariffPricesPayload;
}

const statusOptions = [
  { value: null, text: 'Все' },
  { value: 'active', text: 'Активные' },
  { value: 'archived', text: 'Архив' },
];
const defaultOptions = [
  { value: null, text: 'Все' },
  { value: true, text: 'По умолчанию' },
  { value: false, text: 'Не по умолчанию' },
];

const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);
const filterStatus = ref<string | null>(null);
const filterIsDefault = ref<boolean | null>(null);

const popupOpen = ref(false);
const popupMode = ref<'create' | 'edit'>('create');
const formData = ref<TariffFormModel>(emptyForm());
const saving = ref(false);

const confirmOpen = ref(false);
const confirmTarget = ref<Tariff | null>(null);

const popupTitle = computed(() =>
  popupMode.value === 'create'
    ? 'Новый тариф'
    : `Тариф «${formData.value.name}» (#${formData.value.id ?? ''})`,
);

function emptyForm(): TariffFormModel {
  return {
    name: '',
    type: '',
    group: '',
    status: 'active',
    is_default: false,
    limits: { sms_speed: 0, call_speed: 0, max_mailings: 0, max_rent_phones: 0 },
    api_smpp_limits: { api_limit: 0, smpp_limit: 0 },
    prices: {},
  };
}

watch([filterStatus, filterIsDefault], () => {
  setTariffListFilters({ status: filterStatus.value, is_default: filterIsDefault.value });
  grid.value?.instance?.refresh();
});

function refresh() {
  grid.value?.instance?.refresh();
}

function openCreate() {
  popupMode.value = 'create';
  formData.value = emptyForm();
  popupOpen.value = true;
}

async function openEdit(row: Tariff) {
  popupMode.value = 'edit';
  try {
    const fresh = (await tariffsStore.byKey?.(row.id)) ?? row;
    const t = fresh as Tariff;
    formData.value = {
      id: t.id,
      name: t.name,
      type: t.type ?? '',
      group: t.group ?? '',
      status: (t.status ?? 'active') as 'active' | 'archived',
      is_default: Boolean(t.is_default),
      limits: {
        sms_speed: t.limits?.sms_speed ?? 0,
        call_speed: t.limits?.call_speed ?? 0,
        max_mailings: t.limits?.max_mailings ?? 0,
        max_rent_phones: t.limits?.max_rent_phones ?? 0,
      },
      api_smpp_limits: {
        api_limit: t.api_smpp_limits?.api_limit ?? 0,
        smpp_limit: t.api_smpp_limits?.smpp_limit ?? 0,
      },
      prices: (t.prices ?? {}) as TariffPricesPayload,
    };
    popupOpen.value = true;
  } catch (e) {
    showError(e);
  }
}

function confirmDelete(row: Tariff) {
  confirmTarget.value = row;
  confirmOpen.value = true;
}

async function doDelete() {
  if (!confirmTarget.value) return;
  try {
    await tariffsStore.remove?.(confirmTarget.value.id);
    showSuccess('Тариф удалён');
    confirmOpen.value = false;
    confirmTarget.value = null;
    refresh();
  } catch (e) {
    showError(e);
  }
}

async function save() {
  saving.value = true;
  try {
    const m = formData.value;
    const body: TariffCreateRequest | TariffUpdateRequest = {
      name: m.name,
      type: m.type || null,
      group: m.group || null,
      status: m.status,
      is_default: m.is_default,
      limits: m.limits,
      api_smpp_limits: m.api_smpp_limits,
      // prices/buy_prices фактически редактируются в Phase D;
      // на этом этапе пробрасываем существующую payload без изменений.
      prices: m.prices as unknown as TariffCreateRequest['prices'],
    };
    if (popupMode.value === 'create') {
      await tariffsStore.insert(body as unknown as Tariff);
      showSuccess('Тариф создан');
    } else {
      await tariffsStore.update(m.id!, body as unknown as Tariff);
      showSuccess('Тариф обновлён');
    }
    popupOpen.value = false;
    refresh();
  } catch (e) {
    showError(e);
  } finally {
    saving.value = false;
  }
}

const pageSizes = [10, 20, 50, 100];
const { paging, pager } = bottoGridPaging({ allowedPageSizes: pageSizes });

// DevExtreme grid event types are loose; use minimal duck-typed shape.
type GridRowEvent = { data?: Tariff };
type GridButtonEvent = { row?: { data?: Tariff } };

function onSmsPricesUpdate(next: ByCurrency<SmsPriceEntry>): void {
  formData.value.prices = { ...formData.value.prices, sms: next };
}
function onCallsPricesUpdate(next: ByCurrency<CallPriceEntry>): void {
  formData.value.prices = { ...formData.value.prices, calls: next };
}

function handleRowDblClick(e: GridRowEvent): void {
  if (e.data) void openEdit(e.data);
}
function handleEditButton(e: GridButtonEvent): void {
  if (e.row?.data) void openEdit(e.row.data);
}
function handleDeleteButton(e: GridButtonEvent): void {
  if (e.row?.data) confirmDelete(e.row.data);
}
</script>

<template>
  <BottoPagePanel title="Тарифы" standalone>
    <template #actions>
      <DxSelectBox
        v-model:value="filterStatus"
        :items="statusOptions"
        display-expr="text"
        value-expr="value"
        placeholder="Статус"
        width="180"
      />
      <DxSelectBox
        v-model:value="filterIsDefault"
        :items="defaultOptions"
        display-expr="text"
        value-expr="value"
        placeholder="По умолчанию"
        width="200"
      />
      <DxButton icon="add" text="Добавить" type="default" styling-mode="contained" @click="openCreate" />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </template>

    <DxDataGrid
      ref="grid"
      :data-source="tariffsStore"
      key-expr="id"
      :remote-operations="{ paging: true, sorting: true, filtering: false }"
      :show-borders="false"
      :show-row-lines="false"
      :show-column-lines="false"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      :paging="paging"
      :pager="pager"
      height="100%"
      width="100%"
      @row-dbl-click="handleRowDblClick"
    >
      <DxColumn data-field="id" caption="ID" data-type="number" :width="80" />
      <DxColumn data-field="name" caption="Название" />
      <DxColumn data-field="type" caption="Тип" :width="120" />
      <DxColumn data-field="group" caption="Группа" :width="140" />
      <DxColumn data-field="status" caption="Статус" :width="110" />
      <DxColumn data-field="is_default" caption="По умолчанию" data-type="boolean" :width="130" />
      <DxColumn data-field="created_at" caption="Создан" data-type="datetime" :width="160" />
      <DxColumn data-field="updated_at" caption="Обновлён" data-type="datetime" :width="160" />

      <DxColumn type="buttons" :width="100" caption="Действия">
        <DxGridButton hint="Редактировать" icon="edit" :on-click="handleEditButton" />
        <DxGridButton hint="Удалить" icon="trash" :on-click="handleDeleteButton" />
      </DxColumn>

      <DxSorting mode="single" />
      <DxScrolling mode="standard" />
    </DxDataGrid>
  </BottoPagePanel>

    <DxPopup
      v-model:visible="popupOpen"
      :title="popupTitle"
      :show-close-button="true"
      :drag-enabled="false"
      :resize-enabled="false"
      :full-screen="false"
      width="80%"
      height="80%"
    >
      <DxTabPanel :animation-enabled="true">
        <DxTabItem title="Основное">
          <div class="tab-body">
            <DxForm :form-data="formData" :col-count="2" label-location="top">
              <DxSimpleItem
                v-if="popupMode === 'edit'"
                data-field="id"
                :editor-options="{ readOnly: true }"
              />
              <DxSimpleItem data-field="name" :is-required="true">
                <DxLabel text="Название" />
              </DxSimpleItem>
              <DxSimpleItem data-field="type">
                <DxLabel text="Тип" />
              </DxSimpleItem>
              <DxSimpleItem data-field="group">
                <DxLabel text="Группа" />
              </DxSimpleItem>
              <DxSimpleItem
                data-field="status"
                editor-type="dxSelectBox"
                :editor-options="{ items: ['active', 'archived'] }"
              >
                <DxLabel text="Статус" />
              </DxSimpleItem>
              <DxSimpleItem data-field="is_default" editor-type="dxCheckBox">
                <DxLabel text="По умолчанию" />
              </DxSimpleItem>
            </DxForm>
          </div>
        </DxTabItem>

        <DxTabItem title="Лимиты">
          <div class="tab-body">
            <DxForm :form-data="formData.limits" :col-count="2" label-location="top">
              <DxSimpleItem data-field="sms_speed" editor-type="dxNumberBox">
                <DxLabel text="SMS скорость" />
              </DxSimpleItem>
              <DxSimpleItem data-field="call_speed" editor-type="dxNumberBox">
                <DxLabel text="Скорость звонков" />
              </DxSimpleItem>
              <DxSimpleItem data-field="max_mailings" editor-type="dxNumberBox">
                <DxLabel text="Макс. рассылок" />
              </DxSimpleItem>
              <DxSimpleItem data-field="max_rent_phones" editor-type="dxNumberBox">
                <DxLabel text="Макс. номеров аренды" />
              </DxSimpleItem>
            </DxForm>
          </div>
        </DxTabItem>

        <DxTabItem title="SMPP">
          <div class="tab-body">
            <DxForm :form-data="formData.api_smpp_limits" :col-count="2" label-location="top">
              <DxSimpleItem data-field="api_limit" editor-type="dxNumberBox">
                <DxLabel text="API лимит" />
              </DxSimpleItem>
              <DxSimpleItem data-field="smpp_limit" editor-type="dxNumberBox">
                <DxLabel text="SMPP лимит" />
              </DxSimpleItem>
            </DxForm>
          </div>
        </DxTabItem>

        <DxTabItem title="Цены SMS">
          <div class="tab-body">
            <SmsPricesEditor
              :model-value="formData.prices.sms as ByCurrency<SmsPriceEntry> | undefined"
              @update:model-value="onSmsPricesUpdate"
            />
          </div>
        </DxTabItem>

        <DxTabItem title="Цены звонков">
          <div class="tab-body">
            <CallsPricesEditor
              :model-value="formData.prices.calls as ByCurrency<CallPriceEntry> | undefined"
              @update:model-value="onCallsPricesUpdate"
            />
          </div>
        </DxTabItem>
      </DxTabPanel>

      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Сохранить', type: 'default', stylingMode: 'contained', disabled: saving, onClick: save }"
      />
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Отмена', stylingMode: 'outlined', onClick: () => (popupOpen = false) }"
      />
    </DxPopup>

    <ConfirmDialog
      v-if="confirmOpen"
      v-model:visible="confirmOpen"
      title="Удалить тариф?"
      :message="`Тариф «${confirmTarget?.name}» (#${confirmTarget?.id}) будет удалён.`"
      ok-text="Удалить"
      ok-type="danger"
      @ok="doDelete"
    />
</template>

<style scoped>
.tab-body { padding: 16px 4px 0; }
.tab-body.placeholder { color: var(--text-muted, #6b7280); padding: 32px 16px; }
</style>
